<template>
  <div class="min-h-screen bg-slate-50">
    <!-- Common Header -->
    <HeaderComponent />

    <!-- Main Content -->
    <main class="py-8">
      <div class="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div v-if="article" class="grid grid-cols-1 xl:grid-cols-8 gap-8">
          <!-- Left Ad Sidebar -->
          <div class="hidden xl:block xl:col-span-1">
            <div class="sticky top-20">
              <div
                class="bg-gradient-to-br from-blue-100 to-blue-200 border-2 border-dashed border-blue-300 rounded-lg p-4 text-center min-h-[600px] w-[120px] flex flex-col justify-center mx-auto"
              >
                <div class="text-blue-600 text-xs font-medium mb-2">Ad Space</div>
                <div class="text-blue-500 text-xs">120x600</div>
              </div>
            </div>
          </div>

          <!-- Main Content Area -->
          <div class="xl:col-span-6">
            <div class="flex flex-col lg:flex-row gap-8">
              <!-- Sidebar Navigation -->
              <aside v-if="headers.length" class="lg:w-1/4 order-2 lg:order-1">
                <div class="sticky top-24 bg-white rounded-xl shadow-lg p-6 border border-slate-200">
                  <h3 class="font-semibold text-slate-900 mb-4 text-sm uppercase tracking-wide">Article Navigation</h3>
                  <nav class="space-y-2">
                    <a
                      v-for="header in headers"
                      :key="header.id"
                      :href="'#' + header.id"
                      class="block text-sm text-slate-600 hover:text-blue-600 hover:bg-blue-50 px-3 py-2 rounded-lg transition-all duration-200"
                    >
                      {{ header.text }}
                    </a>
                  </nav>
                </div>
              </aside>

              <section class="flex-1 order-1 lg:order-2">
                <!-- Breadcrumb Navigation -->
                <Breadcrumb v-if="article" :section="article.section" :title="article.title" class="mb-6" />

                <!-- Article Header -->
                <div class="bg-white rounded-xl shadow-lg p-8 mb-8 border border-slate-200">
                  <h1 class="heading-xl text-slate-900 mb-4 leading-tight">{{ article.title }}</h1>
                  <div class="flex flex-wrap items-center gap-4 text-sm text-slate-600 mb-6">
                    <span class="flex items-center">
                      <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                        ></path>
                      </svg>
                      By {{ article.author }}
                    </span>
                    <span class="flex items-center">
                      <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        ></path>
                      </svg>
                      {{ article.date }}
                    </span>
                  </div>

                  <div class="relative overflow-hidden rounded-lg mb-6">
                    <img
                      :src="article.imageBase64 || article.imageUrl"
                      :alt="article.title"
                      loading="lazy"
                      decoding="async"
                      width="800"
                      height="400"
                      class="w-full h-64 md:h-96 object-cover"
                    />
                  </div>

                  <div v-if="article.tags && article.tags.length" class="flex flex-wrap gap-2 mb-6">
                    <span v-for="(tag, index) in article.tags" :key="index" class="px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full">
                      {{ tag }}
                    </span>
                  </div>
                </div>

                <!-- Article Content -->
                <div class="bg-white rounded-xl shadow-lg p-8 border border-slate-200">
                  <!-- Ad Placement - Top of Article -->
                  <div class="mb-8">
                    <div class="bg-gradient-to-r from-purple-100 to-indigo-100 border-2 border-dashed border-purple-300 rounded-lg p-6 text-center">
                      <div class="text-purple-600 text-sm font-medium mb-2">Advertisement</div>
                      <div class="text-purple-500 text-xs">728x90 Leaderboard</div>
                    </div>
                  </div>

                  <div
                    class="prose prose-slate max-w-none prose-headings:text-slate-900 prose-headings:font-bold prose-h2:text-xl prose-h2:mt-8 prose-h2:mb-4 prose-p:text-slate-700 prose-p:leading-relaxed prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline"
                    v-html="processedContent"
                  ></div>

                  <!-- Ad Placement - Bottom of Article -->
                  <div class="mt-8 pt-8 border-t border-slate-200">
                    <div class="flex justify-center">
                      <div
                        class="bg-gradient-to-br from-orange-100 to-red-100 border-2 border-dashed border-orange-300 rounded-lg p-6 text-center w-[320px] h-[270px] flex flex-col justify-center"
                      >
                        <div class="text-orange-600 text-sm font-medium mb-2">Advertisement</div>
                        <div class="text-orange-500 text-xs">300x250 Medium Rectangle</div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </div>

          <!-- Right Ad Sidebar -->
          <div class="hidden xl:block xl:col-span-1">
            <div class="sticky top-20">
              <div
                class="bg-gradient-to-br from-green-100 to-green-200 border-2 border-dashed border-green-300 rounded-lg p-4 text-center min-h-[600px] w-[120px] flex flex-col justify-center mx-auto"
              >
                <div class="text-green-600 text-xs font-medium mb-2">Ad Space</div>
                <div class="text-green-500 text-xs">120x600</div>
              </div>
            </div>
          </div>
        </div>

        <section v-else class="text-center py-16">
          <div class="bg-white rounded-xl shadow-lg p-12 border border-slate-200">
            <svg class="w-16 h-16 mx-auto text-slate-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              ></path>
            </svg>
            <h2 class="heading-lg text-slate-900 mb-2">Article Not Found</h2>
            <p class="text-slate-600">The requested article could not be found.</p>
          </div>
        </section>
      </div>
    </main>

    <!-- Common Footer -->
    <FooterComponent />
  </div>
</template>

<script setup>
  import { ref, computed, onMounted, watch } from 'vue';
  import { useRoute } from 'vue-router';
  import { useHead } from '@vueuse/head';
  import HeaderComponent from '@/components/Header/Header.vue';
  import FooterComponent from '@/components/Footer/Footer.vue';
  import Breadcrumb from '@/components/Common/Breadcrumb.vue';
  import { getAllArticles } from '@/utils/dataProcessor.js';

  // Local state for the currently loaded article
  const article = ref(null);
  const route = useRoute();

  onMounted(() => {
    const slug = route.params.slug;
    const allArticles = getAllArticles();
    article.value = allArticles.find((item) => item.slug === slug) || null;
    window.scrollTo({ top: 0 });
  });

  // Dynamic meta tags with SEO optimization
  watch(
    article,
    (newArticle) => {
      if (newArticle) {
        useHead({
          title: `${newArticle.title} | Hardware Pulse`,
          meta: [
            {
              name: 'description',
              content: newArticle.description?.substring(0, 160) || 'Read the latest tech reviews and hardware analysis at Hardware Pulse.',
            },
            { property: 'og:title', content: newArticle.title },
            { property: 'og:description', content: newArticle.description?.substring(0, 160) },
            { property: 'og:url', content: `https://hardwarepulse.com/article/${newArticle.slug}` },
            { property: 'og:image', content: newArticle.imageUrl || 'https://hardwarepulse.com/src/assets/images/logo-static.svg' },
            { property: 'og:type', content: 'article' },
            { property: 'og:site_name', content: 'Hardware Pulse' },
            { name: 'twitter:card', content: 'summary_large_image' },
            { name: 'twitter:title', content: newArticle.title },
            { name: 'twitter:description', content: newArticle.description?.substring(0, 160) },
            { name: 'twitter:image', content: newArticle.imageUrl },
            { name: 'keywords', content: newArticle.tags?.join(', ') || 'hardware, tech, reviews, gaming' },
            { name: 'author', content: newArticle.author },
            { property: 'article:author', content: newArticle.author },
            { property: 'article:published_time', content: newArticle.date },
            { property: 'article:modified_time', content: newArticle.lastModified || newArticle.date },
            { property: 'article:section', content: newArticle.section },
            { property: 'article:tag', content: newArticle.tags?.join(',') },
          ],
          link: [{ rel: 'canonical', href: `https://hardwarepulse.com/article/${newArticle.slug}` }],
          script: [
            {
              type: 'application/ld+json',
              children: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'Article',
                headline: newArticle.title,
                description: newArticle.description,
                image: newArticle.imageUrl || 'https://hardwarepulse.com/src/assets/images/logo-static.svg',
                author: {
                  '@type': 'Person',
                  name: newArticle.author,
                },
                publisher: {
                  '@type': 'Organization',
                  name: 'Hardware Pulse',
                  logo: {
                    '@type': 'ImageObject',
                    url: 'https://hardwarepulse.com/src/assets/images/logo-static.svg',
                  },
                },
                datePublished: newArticle.date,
                dateModified: newArticle.lastModified || newArticle.date,
                mainEntityOfPage: {
                  '@type': 'WebPage',
                  '@id': `https://hardwarepulse.com/article/${newArticle.slug}`,
                },
                articleSection: newArticle.section,
                keywords: newArticle.tags?.join(', ') || 'hardware, tech, reviews, gaming',
              }),
            },
          ],
        });
      }
    },
    { immediate: true }
  );

  // Process the article content to add IDs to header elements.
  // We assume that the headers are <h2> tags.
  const processedContent = computed(() => {
    if (!article.value || !article.value.content) return '';
    const parser = new DOMParser();
    const doc = parser.parseFromString(article.value.content, 'text/html');

    // Find all <h2> elements and assign an ID based on their text
    const headers = doc.querySelectorAll('h2');
    headers.forEach((header) => {
      const text = header.textContent.trim();
      const id = text.toLowerCase().replace(/\s+/g, '-');
      header.setAttribute('id', id);
    });

    return doc.body.innerHTML;
  });

  // Extract headers info to build the sidebar nav.
  // This runs on the processed content so that the headers have IDs.
  const headers = computed(() => {
    if (!article.value || !article.value.content) return [];
    const parser = new DOMParser();
    const doc = parser.parseFromString(processedContent.value, 'text/html');
    const headerNodes = doc.querySelectorAll('h2');
    return Array.from(headerNodes).map((header) => {
      const text = header.textContent.trim();
      const id = header.getAttribute('id');
      return { text, id };
    });
  });
</script>

<style scoped>
  /* Tailwind CSS handles all styling with @tailwindcss/typography for prose content */
</style>
